import { AuthenticatedComponentGuard } from "@src/auth/authenticated-component-guard";
import { AgentChatHost } from "@src/components/agent-chat/agent-chat-host";
import { useMyWhiteboardsProjectedSubscription } from "@src/components/board/subscriptions/my-whiteboards-projected";
import { MenuStateProvider } from "@src/components/layouts/menu/menu-state-provider";
import { useWhatsAppConnectedSubscription } from "@src/components/modals/whatsapp-integration-modal/hooks/use-whatsapp-connected-subscription";
import { usePlanStatus } from "@src/components/plan/subscriptions/plan-status";
import { DesktopInviteBanner } from "@src/desktop";
import { useGAIdentification } from "@src/hooks/use-ga-identification";
import { useGAPageView } from "@src/hooks/use-ga-page-view";
import { useIsDesktop } from "@src/hooks/use-is-desktop";
import { usePosthogIdentification } from "@src/hooks/use-posthog-identification";
import { usePostHogPageView } from "@src/hooks/use-posthog-page-view";
import { useRouteScrollRestoration } from "@src/hooks/use-route-scroll-restoration";
import { memo, useMemo, useRef } from "react";
import * as portals from "react-reverse-portal";
import { Outlet, useLocation } from "react-router-dom";
import { NavPendingRenameProvider } from "../navigation/context/nav-pending-rename-context";
import { FloatingHelpButton } from "../navigation/floating-help-button";
import { NavBar as NavBarComponent } from "../navigation/nav-bar";
import { AfterOnboarding } from "../onboarding/after-onboarding";
import { OnboardingIntroVideos } from "../onboarding/onboarding-intro-videos";
import { ReferralWelcomePopup } from "../onboarding/referral-welcome-popup";
import { UpgradeNotice } from "../plan/plan-modal/upgrade-notice";
import { PostLoginGraduation } from "../plan/post-login-graduation";
import { TrialBanner } from "../plan/trial-banner";
import { MenuBar, MenuDrawer } from "./menu/menu-components";
import { MantineProvider } from "./providers/mantine-provider";
import { SideBar } from "./sidebar/sidebar-components";

export const AuthenticatedLayout = memo(() => {
	const location = useLocation();
	const mainScrollRef = useRef<HTMLDivElement>(null);
	useGAIdentification();
	useGAPageView();
	usePosthogIdentification();
	usePostHogPageView();
	usePlanStatus();
	useMyWhiteboardsProjectedSubscription();
	useWhatsAppConnectedSubscription();

	useRouteScrollRestoration(mainScrollRef, location.pathname);

	// We must use JS for this as the out portal can only render once
	// or it might fail to render at all. This breakpoint needs to be
	// kept in sync with the breakpoint in the sidebar-components.tsx file. (tailwind)
	const isDesktop = useIsDesktop();

	const navBar = useMemo(
		() => (
			<AuthenticatedComponentGuard>
				<NavBarComponent innerScroll={isDesktop} />
			</AuthenticatedComponentGuard>
		),
		[isDesktop],
	);

	// Render the portal node once so it doesn't unmount and maintains the local state
	const portalNode = useMemo(() => portals.createHtmlPortalNode(), []);

	// adds a DOM element to render out to so we need
	// to set the height to full to avoid layout issues
	portalNode.element.setAttribute("class", "h-full");

	// Because of the way the portal is rendered, we need to disable the dev warnings
	// otherwise they'll spam the console. Only way around this would be to change the DND
	// library to a different one. I've tried many things but it still works so I'm not
	// going to change it.
	// @ts-expect-error ignore
	window["__@hello-pangea/dnd-disable-dev-warnings"] = true;

	return (
		<MantineProvider>
			<NavPendingRenameProvider>
				<MenuStateProvider isDesktop={isDesktop}>
					<portals.InPortal node={portalNode}>{navBar}</portals.InPortal>

					<div className="flex flex-col h-dvh landscape:flex-row desktop-nav:flex-row">
						{isDesktop && (
							<AuthenticatedComponentGuard>
								<SideBar>
									<portals.OutPortal node={portalNode} />
								</SideBar>
							</AuthenticatedComponentGuard>
						)}

						{!isDesktop && (
							<AuthenticatedComponentGuard>
								<MenuBar />
							</AuthenticatedComponentGuard>
						)}

						{/* min-w-0: let this flex child shrink when agent
						    drawer is open instead of overflowing the viewport */}
						<div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
							<DesktopInviteBanner />
							<AuthenticatedComponentGuard>
								<div className="shrink-0">
									<TrialBanner />
								</div>
							</AuthenticatedComponentGuard>
							<div className="relative flex min-h-0 min-w-0 flex-1">
								{/* Container first in DOM so portal target exists when Host renders */}
								<div
									id="agent-chat-drawer-container"
									className="relative order-2 h-full shrink-0"
								/>
								<div
									ref={mainScrollRef}
									className="relative order-1 flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto"
								>
									<AuthenticatedComponentGuard
										loadingComponent={<Outlet key={location.pathname} />}
									>
										<AgentChatHost>
											<Outlet key={location.pathname} />
										</AgentChatHost>
									</AuthenticatedComponentGuard>

									{!isDesktop && (
										<AuthenticatedComponentGuard>
											<MenuDrawer>
												<portals.OutPortal node={portalNode} />
											</MenuDrawer>
										</AuthenticatedComponentGuard>
									)}
								</div>
							</div>
						</div>
					</div>

					<AuthenticatedComponentGuard>
						<FloatingHelpButton />
						<PostLoginGraduation />
						<UpgradeNotice />
						<OnboardingIntroVideos />
						<ReferralWelcomePopup />
						<AfterOnboarding />
					</AuthenticatedComponentGuard>
				</MenuStateProvider>
			</NavPendingRenameProvider>
		</MantineProvider>
	);
});
